// Trust → Terms & Privacy — the legal plane, for a super-admin.
//
// Team, 10 Sep: "On IOT-admin the super-admins should be able to manage them
// too properly. This needs to be done for both, media.pdooh.ai and pdooh.ai."
//
// Before this there was no legal surface here at all. The only read of either
// acceptance table anywhere in this console was a COUNT in the tenant drawer,
// and it was VERSION-BLIND — a workspace whose only acceptance was a superseded
// version rendered as compliant. A compliance screen that shows false green is
// worse than no screen, so everything here names the version it is judging.
//
// ── IT IS NOT THE PAYOUTS "AGREEMENTS" SCREEN ───────────────────────────────
// Two different things share the word and this console already carries both.
// Payouts → Agreements is the COMMERCIAL plane: the rates a payout bills
// against. This is the LEGAL plane: the Terms and the Privacy Policy a person
// or a workspace accepted. Nothing here touches money, and it lives under Trust
// — "who may become a device, or a person" — because that is the question it
// answers about a customer.
//
// ── THE THREE THINGS THIS SCREEN EXISTS TO SAY ──────────────────────────────
//  1. WHICH VERSION IS ACTUALLY IN FORCE, which is not simply the newest one
//     whose date has passed: a release whose notices have not gone out is
//     deliberately NOT enforced by either product, because both agreements
//     promise notice before a change takes effect. The badge says "in force"
//     only where the running system agrees.
//  2. WHAT A VERSION'S TEXT WAS. The archive holds the document, not just the
//     label — and where a label covered more than one document (the Brands
//     Manager's 2026-08-20 covers three), the row says so instead of quietly
//     showing the newest.
//  3. WHO HAS ACCEPTED, against the version in force rather than against
//     "anything at all".

import React, { useState } from 'react';
import {
  PageHead, Loading, ErrorState, Pill, Button, Empty, DataTable,
} from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import c from './Trust.module.css';

const PRODUCTS = [
  { id: 'media_console', label: 'Media Owner Console' },
  { id: 'brands_manager', label: 'Brands Manager' },
];

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
  : '—');

// ── Publish ─────────────────────────────────────────────────────────────────
// The form carries NO rules of its own. The notice window, the past-date
// refusal, the signed-waiver requirement and the immutability of an
// announcement are all enforced by V355's trigger and constraints, and the
// server hands their sentences straight back. Re-implementing them here would
// create a second set to drift from the first — and the one that matters is
// the one the database will actually apply.
function PublishForm({ onDone }) {
  // THE TWO CHOICES A SUPER-ADMIN ACTUALLY MAKES, as choices rather than as
  // side effects of three checkboxes:
  //
  //   notice   'email'  every customer is emailed and the change waits a week
  //            'silent' nobody is emailed and it can take effect today
  //   when     a date, with Today one tap away
  //
  // They map onto the release row the database already enforces: 'silent' is
  // material=false (a change that owes no notice), and an 'email' change dated
  // inside the week needs a signed, explained waiver. Nothing here re-checks
  // those rules — V355's trigger owns them and its sentences come straight
  // back — because a second copy of a rule is a copy that drifts.
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10); // IST
  const weekOut = new Date(Date.now() + 5.5 * 3600e3 + 8 * 864e5).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    product: 'media_console', version: '', effectiveFrom: weekOut, whatsChanged: '',
    notice: 'email', waiveReason: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const silent = form.notice === 'silent';
  const immediate = form.effectiveFrom === today;
  // An emailed change dated inside the week is the only case that needs a
  // reason. The form ASKS for it rather than letting the server refuse blind.
  const needsWaiver = !silent && form.effectiveFrom && form.effectiveFrom < weekOut;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post('/v2/agreements/releases', {
        product: form.product,
        version: form.version,
        effectiveFrom: form.effectiveFrom,
        whatsChanged: form.whatsChanged,
        // silent === a change that owes no notice, so no email is created and
        // no week is required.
        material: !silent,
        waiveNotice: needsWaiver,
        waiveReason: needsWaiver ? form.waiveReason : '',
      });
      setForm((f) => ({ ...f, version: '', whatsChanged: '', waiveReason: '' }));
      onDone();
    } catch (e2) {
      setErr(e2?.response?.data?.error?.message || e2?.message || 'Could not publish.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className={c.form}>
      <div className={c.formGrid}>
        <label className={c.field}>
          <span className={c.label}>Product</span>
          <select className={c.input} value={form.product} onChange={set('product')}>
            {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label className={c.field}>
          <span className={c.label}>Version</span>
          <input className={`${c.input} ${c.mono}`} value={form.version} onChange={set('version')}
            placeholder="2026-10-01" required />
        </label>
      </div>

      {/* ── THE MASTER SWITCH ────────────────────────────────────────────── */}
      <div className={c.choice}>
        <span className={c.choiceHead}>Notice</span>
        <label className={c.radio}>
          <input type="radio" name="notice" value="email" checked={!silent} onChange={set('notice')} />
          <span>
            <span className={c.radioStrong}>Email every customer</span>
            {' '}— and hold the change until a week after they are told.
          </span>
        </label>
        <label className={c.radio}>
          <input type="radio" name="notice" value="silent" checked={silent} onChange={set('notice')} />
          <span>
            <span className={c.radioStrong}>Do not email anyone</span>
            {' '}— for a correction that does not change the deal.
          </span>
        </label>
        {silent && (
          <p className={c.choiceNote}>
            No notice is sent and none is owed, so this can take effect today. It is recorded as a
            silent release and shows as one in the list below — customers are still re-asked to
            accept the new version when they next open the product.
          </p>
        )}
      </div>

      {/* ── WHEN, WITH IMMEDIATE ONE TAP AWAY ───────────────────────────── */}
      <div className={c.formRow}>
        <label className={c.field}>
          <span className={c.label}>Takes effect</span>
          <input className={c.input} type="date" value={form.effectiveFrom}
            onChange={set('effectiveFrom')} required />
        </label>
        <Button
          type="button"
          onClick={() => setForm((f) => ({ ...f, effectiveFrom: today }))}
        >
          Today
        </Button>
        <Button
          type="button"
          onClick={() => setForm((f) => ({ ...f, effectiveFrom: weekOut }))}
        >
          In a week
        </Button>
        {immediate && (
          <span className={c.inlineNote}>In force as soon as it is published.</span>
        )}
      </div>

      <label className={c.field}>
        <span className={c.label}>What is changing</span>
        <textarea
          className={c.textarea}
          value={form.whatsChanged}
          onChange={set('whatsChanged')}
          rows={2}
          placeholder="Payout confirmation moves to 30 days after month end."
        />
      </label>
      <span className={c.inlineNote}>
        {silent
          ? 'Nobody is emailed, but this is the record of what changed and why.'
          : 'Customers read this sentence in the notice email. Write it for them.'}
      </span>

      {/* Only the one case that needs it. */}
      {needsWaiver && (
        <div className={c.warnBox}>
          <span className={c.warnTitle}>This is sooner than a week after the notice.</span>
          <p className={c.warnBody}>
            Customers are still emailed, but they get less than the week the agreement promises.
            Say why — it is recorded against your name.
          </p>
          <input
            className={c.input}
            value={form.waiveReason}
            onChange={set('waiveReason')}
            placeholder="Regulator deadline of 14 Oct"
            required
          />
        </div>
      )}

      {err && <div className={c.errBar}>{err}</div>}
      <div>
        <Button primary disabled={busy}>
          {busy ? 'Publishing…'
            : silent && immediate ? 'Publish — in force now, no email'
              : silent ? 'Publish — no email'
                : immediate ? 'Publish — in force now, customers emailed'
                  : 'Publish'}
        </Button>
      </div>
    </form>
  );
}

export default function Legal() {
  const [product, setProduct] = useState('media_console');
  const [publishing, setPublishing] = useState(false);

  const releases = useApi(() => api.get('/v2/agreements/releases').then((r) => r.data), []);
  const register = useApi(
    () => api.get(`/v2/agreements/register?product=${product}`).then((r) => r.data), [product],
  );

  const reloadAll = () => { releases.reload(); register.reload(); setPublishing(false); };

  if (releases.loading) return <Loading what="the agreement plane" />;
  if (releases.error) return <ErrorState error={releases.error} onRetry={releases.reload} />;

  const rows = (releases.data?.releases) || [];
  const held = rows.filter((r) => r.due && !r.noticeSettled);

  return (
    <div>
      <PageHead
        title="Terms & Privacy"
        sub="The versions in force, the text behind each one, and who has accepted."
        actions={<Button primary onClick={() => setPublishing((v) => !v)}>
          {publishing ? 'Close' : 'Publish a version'}
        </Button>}
      />

      {/* A DUE RELEASE THAT IS NOT BEING ENFORCED is the one thing on this
          screen that needs a human today: the products are holding the old
          terms because the notice never went out, which is the safe failure —
          and a silent one. */}
      {held.length > 0 && (
        <div className={c.heldBox}>
          <strong>{held.length} release{held.length === 1 ? '' : 's'} due but not in force.</strong>{' '}
          Both products refuse to enforce a change whose notices have not all been sent, because the
          agreement promises notice first. The previous version stays in force until the notice run
          completes.
          <ul>
            {held.map((r) => (
              <li key={r.id}>
                {r.productLabel} {r.version} — due {fmtDate(r.effective_from)},{' '}
                {r.notices_sent} of {r.notices_total} notices sent
                {r.notices_failed > 0 ? `, ${r.notices_failed} failed` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {publishing && (
        <div className={c.card}>
          <PublishForm onDone={reloadAll} />
        </div>
      )}

      <h3>Releases</h3>
      {rows.length === 0
        ? <Empty title="No releases yet">Apply V355 and seed the archive to see the versions already in force.</Empty>
        : (
          <DataTable
            getKey={(r) => r.id}
            rows={rows}
            columns={[
              { key: 'productLabel', label: 'Product' },
              { key: 'version', label: 'Version' },
              {
                key: 'state',
                label: 'State',
                render: (r) => (r.enforced
                  ? <Pill tone="grow">In force</Pill>
                  : r.due
                    ? <Pill tone="bad">Held — notice incomplete</Pill>
                    : <Pill tone="pend">Scheduled</Pill>),
              },
              { key: 'effective_from', label: 'Takes effect', render: (r) => fmtDate(r.effective_from) },
              { key: 'announced_at', label: 'Announced', render: (r) => fmtDate(r.announced_at) },
              {
                key: 'notices',
                label: 'Notices',
                // A SILENT RELEASE MUST READ AS ONE. This is the audit column,
                // and "no email was sent" rendered as a blank or a 0/0 is the
                // kind of absence that later reads as an oversight rather than
                // a decision somebody took on purpose.
                render: (r) => (r.published_by === 'backfill'
                  ? <span className={c.faint}>history</span>
                  : !r.material
                    ? <Pill tone="iris">Silent — no email</Pill>
                    : r.waived
                      ? <span title={r.notice_waived_reason}>
                        waived{r.waived_by_name ? ` by ${r.waived_by_name}` : ''}
                      </span>
                      : `${r.notices_sent}/${r.notices_total}`),
              },
              { key: 'acceptances', label: 'Accepted by' },
              {
                key: 'archived_texts',
                label: 'Archived text',
                // MORE THAN ONE TEXT UNDER ONE LABEL is the finding this whole
                // plane was built for. It is never collapsed to a tick.
                render: (r) => (r.archived_texts === 0
                  ? <span className={c.faint}>not archived</span>
                  : r.ambiguous
                    ? <Pill tone="bad">{r.archived_texts} texts — ambiguous</Pill>
                    : `${r.archived_texts} documents`),
              },
              {
                key: 'published_by',
                label: 'Published by',
                render: (r) => (r.published_by === 'backfill'
                  ? 'backfill'
                  : r.published_by_name || r.published_by_email || '—'),
              },
            ]}
          />
        )}

      <h3 className={c.sectionHead} style={{ marginTop: 'var(--s5)' }}>Who has accepted</h3>
      <div className={c.rowActions}>
        {PRODUCTS.map((p) => (
          <Button key={p.id} primary={product === p.id} onClick={() => setProduct(p.id)}>{p.label}</Button>
        ))}
      </div>

      {register.loading ? <Loading what="the register" />
        : register.error ? <ErrorState error={register.error} onRetry={register.reload} />
          : (
            <>
              <p className={c.sectionNote}>
                Judged against{' '}
                <strong>{register.data?.currentVersion || 'no version in force'}</strong>
                {' — '}{register.data?.total} workspace{register.data?.total === 1 ? '' : 's'} on this product.
              </p>
              <DataTable
                getKey={(w) => w.tenant_id}
                rows={register.data?.workspaces || []}
                filterOn={['tenant_name', 'accepted_by_name', 'accepted_by_email']}
                filterPlaceholder="Find a workspace…"
                columns={[
                  { key: 'tenant_name', label: 'Workspace' },
                  {
                    key: 'state',
                    label: 'State',
                    render: (w) => (w.state === 'current' ? <Pill tone="grow">Current</Pill>
                      : w.state === 'behind' ? <Pill tone="bad">Behind</Pill>
                        : <Pill tone="pend">Never accepted</Pill>),
                  },
                  { key: 'version', label: 'Version held', render: (w) => w.version || '—' },
                  { key: 'accepted_at', label: 'Accepted', render: (w) => fmtDate(w.accepted_at) },
                  { key: 'accepted_by_name', label: 'Signed by', render: (w) => w.accepted_by_name || w.accepted_by_email || '—' },
                  {
                    key: 'text_resolvable',
                    label: 'Text on record',
                    // The honest gap. Acceptances written before V355 point at
                    // no release, so the document they agreed to cannot be
                    // reconstructed — and this says so rather than leaving a
                    // blank that reads like "fine".
                    render: (w) => (!w.version ? '—'
                      : w.text_resolvable ? 'yes'
                        : <span title="Recorded before the archive existed — the exact text cannot be reconstructed.">
                          not recoverable
                        </span>),
                  },
                ]}
              />
            </>
          )}
    </div>
  );
}
